'use client'

import { useState, useEffect, useRef } from 'react'
import { Button } from "@/app/components/ui/button"
import { Input } from "@/app/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/app/components/ui/card"
import { Alert, AlertDescription, AlertTitle } from "@/app/components/ui/alert"
import { Terminal, Copy, Search, Sparkles, BookOpen } from "lucide-react" 
import EmailCaptureModal from '@/app/components/EmailCaptureModal'
import { incrementToolUsage } from '@/lib/trackUsage'

interface FactResult {
  hook: string;
  fact: string;
}

export default function ObscureFactFinder() {
  // Define surprise topics at the component level
  const surpriseTopics = [
    "platypus", "ancient Rome", "deep sea creatures", "quantum physics", 
    "coffee", "dreams", "origami", "meteorites", "libraries", 
    "chocolate", "Svalbard", "bioluminescence", "conquistadors", "umbrellas"
  ];
  
  const [topic, setTopic] = useState('')
  const [factResult, setFactResult] = useState<FactResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false)
  const [copied, setCopied] = useState(false)
  const isRandomTopicRef = useRef(false);

  // Email capture logic
  useEffect(() => {
    const submitted = localStorage.getItem('hasSubmittedEmail') === 'true'
    setHasSubmittedEmail(submitted)
    if (!submitted) {
      const timer = setTimeout(() => setShowEmailModal(true), 2000)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleEmailSuccess = () => {
    setHasSubmittedEmail(true)
    setShowEmailModal(false) // Close modal on success
  }

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault()
    if (!topic.trim()) {
        setError("Please enter a topic.")
        return;
    }

    setLoading(true)
    setError(null)
    setFactResult(null)
    setCopied(false); // Reset copied state

    try {
      await incrementToolUsage('obscure-fact-finder')

      const response = await fetch('/api/obscure-fact-finder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const data: FactResult = await response.json()
      setFactResult(data)

    } catch (err: any) {
       console.error("Fetch error:", err)
       setError(err.message || 'Failed to fetch fact. Please try again.')
    } finally {
      setLoading(false)
      isRandomTopicRef.current = false; // Reset the flag after search completes
    }
  }
  
  const handleCopyToClipboard = () => {
    if (!factResult) return;
    const textToCopy = `${factResult.hook}\n${factResult.fact}`;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      setError("Failed to copy fact to clipboard.");
    });
  };

  const handleSurpriseMe = () => {
    const randomTopic = surpriseTopics[Math.floor(Math.random() * surpriseTopics.length)];
    isRandomTopicRef.current = true; // Set flag to indicate a random topic
    setTopic(randomTopic);
  };

  // Effect to auto-submit when a random topic is selected
  useEffect(() => {
    if (isRandomTopicRef.current && topic && !loading) {
      // Small timeout to ensure state has been updated
      const timer = setTimeout(() => {
        handleSubmit();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [topic, loading]);

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent pb-1">
          Obscure Fact Finder
        </h1>
        <p className="text-lg text-neutral-600 dark:text-neutral-400">
          Unearth weird, surprising facts about any topic. Just ask!
        </p>
      </div>

      <Card className="border-2 border-blue-100 dark:border-blue-900/30">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <label htmlFor="topic-input" className="block text-sm font-medium mb-2 text-neutral-700 dark:text-neutral-300">
                What topic should we dig into?
              </label>
              <div className="relative flex">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                  <Search className="h-5 w-5" />
                </div>
                <Input
                  id="topic-input"
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., snakes, cheese, Roman Empire..."
                  className="pl-10 py-6 rounded-l-md bg-white dark:bg-neutral-900 focus-visible:ring-blue-500"
                  aria-label="Topic input"
                />
                <Button 
                  type="button" 
                  onClick={handleSurpriseMe}
                  variant="outline"
                  className="rounded-none border-l-0 py-6 font-medium flex-shrink-0 hover:bg-blue-50 dark:hover:bg-blue-950 hover:cursor-pointer"
                >
                  <Sparkles className="h-4 w-4 mr-2 text-amber-500" />
                  Surprise Me
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading || !topic.trim()} 
                  className="rounded-l-none py-6 bg-blue-600 hover:bg-blue-700 flex-shrink-0 hover:cursor-pointer"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Digging...
                    </>
                  ) : "Get Fact"}
                </Button>
              </div>
            </div>
          </form>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <Terminal className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && !factResult && (
            <div className="flex items-center justify-center p-8 text-neutral-600 dark:text-neutral-400">
              <div className="relative w-16 h-16">
                <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-200 dark:border-blue-900 rounded-full animate-ping opacity-75"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-500 dark:text-blue-400">
                  <BookOpen className="h-8 w-8" />
                </div>
              </div>
              <span className="ml-4 text-lg font-medium">Consulting the archives...</span>
            </div>
          )}
        </CardContent>
      </Card>

      {factResult && !loading && (
        <Card className="border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-950/40 dark:to-blue-950/40 border-b border-neutral-200 dark:border-neutral-800 relative pb-4">
            <CardTitle className="text-xl font-bold text-indigo-800 dark:text-indigo-300">
              {factResult.hook}
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleCopyToClipboard} 
              className="absolute top-3 right-3 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 hover:bg-white/80 dark:hover:bg-black/20 hover:cursor-pointer"
              aria-label="Copy fact to clipboard"
            >
              <Copy className="h-4 w-4 mr-2" />
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </CardHeader>
          <CardContent className="p-6 bg-white dark:bg-neutral-900">
            <p className="text-lg text-neutral-700 dark:text-neutral-300 leading-relaxed">
              {factResult.fact}
            </p>
            <div className="mt-6 pt-4 border-t border-neutral-100 dark:border-neutral-800 text-xs text-neutral-500 dark:text-neutral-500 flex items-center">
              <Sparkles className="h-3 w-3 mr-1" />
              <span>Generated by AI • May contain inaccuracies</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tool Description Section */}
      <div className="prose dark:prose-invert max-w-none mt-12 pt-8 border-t border-neutral-200 dark:border-neutral-800">
        <h2 className="text-xl font-semibold mb-4">About the Obscure Fact Finder</h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          Ever wanted a bizarre tidbit to break the ice or fuel your curiosity? This tool uses AI to delve into almost any topic you provide and returns a single, delightfully obscure fact. Perfect for content creators looking for hooks, trivia buffs, or anyone who enjoys learning something weird and wonderful.
        </p>
        <h3 className="text-lg font-semibold mt-6 mb-3">Features</h3>
        <ul className="space-y-2 pl-5 text-neutral-600 dark:text-neutral-400">
          <li className="flex items-center gap-2">
            <span className="text-blue-500">✨</span> Generates unique, obscure facts with catchy hooks
          </li>
          <li className="flex items-center gap-2">
            <span className="text-blue-500">🔍</span> Search any topic that interests you
          </li>
          <li className="flex items-center gap-2">
            <span className="text-blue-500">🎲</span> "Surprise Me" button for random discoveries
          </li>
          <li className="flex items-center gap-2">
            <span className="text-blue-500">📋</span> Copy facts to clipboard for easy sharing
          </li>
        </ul>
        <h3 className="text-lg font-semibold mt-6 mb-3">How it Works</h3>
        <p className="text-neutral-600 dark:text-neutral-400">
          Simply type your topic into the input field and click "Get Fact". Our AI assistant consults its vast knowledge base to find a surprising piece of information related to your query, formatted neatly for you.
        </p>
      </div>

      {/* Email capture modal */}
      <EmailCaptureModal
        isOpen={showEmailModal && !hasSubmittedEmail}
        onClose={() => setShowEmailModal(false)}
        onSuccess={handleEmailSuccess}
      />
    </div>
  )
}
